// Thin audio-editing layer on the native Web Audio API.
//
// Everything is decoded through one shared AudioContext, so every clip is
// resampled to the same rate and downmixed to mono — which makes cut / trim /
// copy / insert between different versions trivial and splice-compatible.
// Buffers are treated as immutable: each op returns a NEW AudioBuffer, so the
// undo stack is just a list of previous buffers.

import * as ops from '../shared/sampleOps';

let _ctx;
function ctx() {
  if (!_ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    _ctx = new AC();
  }
  return _ctx;
}

function monoBuffer(float32, sampleRate) {
  const buf = ctx().createBuffer(1, float32.length || 1, sampleRate);
  buf.getChannelData(0).set(float32);
  return buf;
}

function toMono(audioBuffer) {
  if (audioBuffer.numberOfChannels === 1) return audioBuffer;
  const len = audioBuffer.length;
  const n = audioBuffer.numberOfChannels;
  const out = new Float32Array(len);
  for (let c = 0; c < n; c++) {
    const d = audioBuffer.getChannelData(c);
    for (let i = 0; i < len; i++) out[i] += d[i] / n;
  }
  return monoBuffer(out, audioBuffer.sampleRate);
}

const sec2samp = (buf, t) => Math.max(0, Math.min(buf.length, Math.round(t * buf.sampleRate)));

// Fetch-decoded Blob -> mono AudioBuffer at the shared context rate.
export async function loadAudio(blob) {
  const arrayBuffer = await blob.arrayBuffer();
  const decoded = await ctx().decodeAudioData(arrayBuffer);
  return toMono(decoded);
}

export function deleteRegion(buf, start, end) {
  const s = sec2samp(buf, start);
  const e = sec2samp(buf, end);
  if (e <= s) return buf;
  const d = buf.getChannelData(0);
  const out = new Float32Array(buf.length - (e - s));
  out.set(d.subarray(0, s), 0);
  out.set(d.subarray(e), s);
  return monoBuffer(out, buf.sampleRate);
}

export function trimTo(buf, start, end) {
  const s = sec2samp(buf, start);
  const e = sec2samp(buf, end);
  if (e <= s) return buf;
  return monoBuffer(buf.getChannelData(0).slice(s, e), buf.sampleRate);
}

// A silent clip of the same shape as everything else, so it inserts and
// splices like any other piece.
export function silence(seconds, sampleRate) {
  return monoBuffer(new Float32Array(Math.max(1, Math.round(seconds * sampleRate))), sampleRate);
}

export function copyRegion(buf, start, end) {
  return trimTo(buf, start, end);
}

export function insertAt(dest, piece, at) {
  const a = sec2samp(dest, at);
  const d = dest.getChannelData(0);
  const p = piece.getChannelData(0);
  const out = new Float32Array(dest.length + piece.length);
  out.set(d.subarray(0, a), 0);
  out.set(p, a);
  out.set(d.subarray(a), a + piece.length);
  return monoBuffer(out, dest.sampleRate);
}

// Multiply the samples inside [start,end] by `factor` (clamped to [-1,1]).
// Replace dest[outStart..outEnd] with `piece`, overlapping both seams by
// `fadeSec` so neither one clicks. A cut placed on a true zero crossing is
// rare; a few milliseconds of crossfade makes the join inaudible even when the
// boundary is a little off. fadeSec 0 gives a plain butt splice.
export function spliceCrossfade(dest, outStart, outEnd, piece, fadeSec) {
  const { out } = ops.spliceCrossfade(dest.getChannelData(0), dest.sampleRate, outStart, outEnd, piece.getChannelData(0), fadeSec);
  return monoBuffer(out, dest.sampleRate);
}

export function applyGain(buf, start, end, factor) {
  const s = sec2samp(buf, start);
  const e = sec2samp(buf, end);
  const d = buf.getChannelData(0);
  const out = new Float32Array(d.length);
  out.set(d);
  for (let i = s; i < e; i++) {
    let v = out[i] * factor;
    if (v > 1) v = 1; else if (v < -1) v = -1;
    out[i] = v;
  }
  return monoBuffer(out, buf.sampleRate);
}

// Buffers are produced immutably, so a snapshot is just the buffer itself.
export function snapshot(buf) {
  return buf;
}

function encodeWav(channel, sampleRate) {
  return new Blob([ops.encodeWav16(channel, sampleRate)], { type: 'audio/wav' });
}

export async function toWavBlob(buf) {
  return encodeWav(buf.getChannelData(0), buf.sampleRate);
}

// Several spans dropped in one pass — the pause cuts, which can number in
// the dozens on one track. Overlaps are merged; order does not matter.
export function deleteRegions(buf, spans) {
  const out = ops.deleteRegions(buf.getChannelData(0), buf.sampleRate, spans);
  if (out === buf.getChannelData(0)) return buf;
  return monoBuffer(out, buf.sampleRate);
}

// Segments of `buf`, in order, as one clip — `gapBefore` seconds of silence
// in front of a segment where asked. `map` says where each segment sits in
// the clip, so a cursor on the original can follow the clip as it plays.
export function assemble(buf, segments) {
  const sr = buf.sampleRate;
  const d = buf.getChannelData(0);
  const parts = segments
    .map(({ start, end, gapBefore }) => ({ s: sec2samp(buf, start), e: sec2samp(buf, end), gap: Math.round((gapBefore || 0) * sr) }))
    .filter(({ s, e }) => e > s);
  const total = parts.reduce((n, { s, e, gap }) => n + (e - s) + gap, 0);
  const out = new Float32Array(Math.max(1, total));
  const map = [];
  let at = 0;
  parts.forEach(({ s, e, gap }) => {
    at += gap;
    out.set(d.subarray(s, e), at);
    map.push({ from: s / sr, to: e / sr, at: at / sr });
    at += e - s;
  });
  return { buffer: monoBuffer(out, sr), map };
}

// Play a clip straight through the shared context — no waveform, no seeking.
// Returns {stop, position}; `onEnd` fires when the clip runs out by itself.
export function playBuffer(buf, rate, onEnd) {
  const c = ctx();
  if (c.state === 'suspended') c.resume().catch(() => {});
  const src = c.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = rate || 1;
  src.connect(c.destination);
  const t0 = c.currentTime;
  let done = false;
  src.onended = () => {
    if (done) return;
    done = true;
    try { src.disconnect(); } catch (e) { /* already gone */ }
    if (onEnd) onEnd();
  };
  src.start();
  return {
    stop() {
      if (done) return;
      done = true;
      try { src.stop(); } catch (e) { /* not started */ }
      try { src.disconnect(); } catch (e) { /* already gone */ }
    },
    position() { return (c.currentTime - t0) * (rate || 1); },
  };
}
